import axios, { AxiosResponse } from 'axios';
import { API_BASE_URI } from 'library/constants/api';

// Base URLs - resolve at request time so hostname-based logic works
const getCalendarBaseUrl = () => `${API_BASE_URI}/host/calendar`;

// Type definitions
interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  result: T;
  data?: T;
  responseInformation?: {
    isSuccess: boolean;
    responseMessage: string;
    responseCode: number;
  };
}

interface SinglePaginatedResponse<T> {
  result: T;
  responseInformation: {
    isSuccess: boolean;
    responseMessage: string;
    responseCode: number;
  };
}

export interface HostCalendarDayDTO {
  date: string; // Format: "2026-02-01"
  status: 'AVAILABLE' | 'BLOCKED' | 'BOOKED';
  minNights: number;
  price?: number;
  // PriceLabs-synced restrictions — absent on older cached responses, so treat missing
  // as allowed (true), matching the backend's own default.
  checkInAllowed?: boolean;
  checkOutAllowed?: boolean;
  // Per-day cancellation policy catalog code; null/absent means "inherit property default".
  cancellationPolicyCode?: string | null;
  // True when Unpaqd booked a BOOKED date; false when it was booked elsewhere (Airbnb, an iCal-synced platform).
  // Absent on an older backend.
  bysBooking?: boolean;
}

export interface CancellationPolicyRangeRequest {
  fromDate: string; // Format: "2026-03-01"
  toDate: string; // Format: "2026-03-05"
  cancellationPolicyCode: string;
}

export interface BlockRequest {
  from: string; // Format: "2026-03-01"
  to: string; // Format: "2026-03-05"
}

export interface PropertyCalendarCreateRequest {
  defaultMinNights: number;
}

export interface RulesRequest {
  from: string;
  to: string;
  minNights: number;
}

/** Request body for importing / syncing from an Airbnb listing URL (host calendar). */
export interface AirbnbCalendarSyncRequest {
  airbnbListingUrl: string;
  syncMode: 'availability' | 'pricing' | 'both';
}

// Calendar Service
const CalendarService = {
  /**
   * Get calendar days for a property (Host-facing)
   * @param propertyId - The property ID
   * @returns Promise with list of calendar days
   */
  getHostCalendar: async (
    propertyId: string,
  ): Promise<SinglePaginatedResponse<HostCalendarDayDTO[]>> => {
    try {
      const url = `${getCalendarBaseUrl()}/${propertyId}?_t=${Date.now()}`;
      console.log('🔍 Calendar API: GET', url, 'PropertyID:', propertyId);
      const response: AxiosResponse<SinglePaginatedResponse<HostCalendarDayDTO[]>> =
        await axios.get(url, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
          },
        });
      console.log('✅ Calendar API success:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Calendar API error:', error.response?.status, error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Block dates on calendar
   * @param propertyId - The property ID
   * @param blockRequest - Date range to block
   * @returns Promise with response
   */
  blockDates: async (
    propertyId: string,
    blockRequest: BlockRequest,
  ): Promise<SinglePaginatedResponse<any>> => {
    try {
      const response: AxiosResponse<SinglePaginatedResponse<any>> =
        await axios.post(
          `${getCalendarBaseUrl()}/${propertyId}/block`,
          blockRequest,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
              'Content-Type': 'application/json',
            },
          },
        );
      return response.data;
    } catch (error) {
      console.error('Error blocking dates:', error);
      throw error;
    }
  },

  /**
   * Unblock dates on calendar
   * @param propertyId - The property ID
   * @param blockRequest - Date range to unblock
   * @returns Promise with response
   */
  unblockDates: async (
    propertyId: string,
    blockRequest: BlockRequest,
  ): Promise<SinglePaginatedResponse<any>> => {
    try {
      const response: AxiosResponse<SinglePaginatedResponse<any>> =
        await axios.post(
          `${getCalendarBaseUrl()}/${propertyId}/unblock`,
          blockRequest,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
              'Content-Type': 'application/json',
            },
          },
        );
      return response.data;
    } catch (error) {
      console.error('Error unblocking dates:', error);
      throw error;
    }
  },

  /**
   * Create default calendar for property (first time onboarding)
   * @param propertyId - The property ID
   * @param request - Default calendar settings
   * @returns Promise with response
   */
  createDefaultCalendar: async (
    propertyId: string,
    request: PropertyCalendarCreateRequest,
  ): Promise<SinglePaginatedResponse<any>> => {
    try {
      const response: AxiosResponse<SinglePaginatedResponse<any>> =
        await axios.post(
          `${getCalendarBaseUrl()}/${propertyId}/createDefaultCalendar`,
          request,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
              'Content-Type': 'application/json',
            },
          },
        );
      return response.data;
    } catch (error) {
      console.error('Error creating default calendar:', error);
      throw error;
    }
  },

  /**
   * Update default calendar values (Edit property flow)
   * @param propertyId - The property ID
   * @param request - Updated default settings
   * @returns Promise with response
   */
  updateDefaultValues: async (
    propertyId: string,
    request: PropertyCalendarCreateRequest,
  ): Promise<SinglePaginatedResponse<any>> => {
    try {
      const response: AxiosResponse<SinglePaginatedResponse<any>> =
        await axios.post(
          `${getCalendarBaseUrl()}/${propertyId}/updateDefaultValues`,
          request,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
              'Content-Type': 'application/json',
            },
          },
        );
      return response.data;
    } catch (error) {
      console.error('Error updating default values:', error);
      throw error;
    }
  },

  /**
   * Update date-level rules (e.g., minimum nights)
   * @param propertyId - The property ID
   * @param rulesRequest - Rules to apply
   * @returns Promise with response
   */
  updateRules: async (
    propertyId: string,
    rulesRequest: RulesRequest,
  ): Promise<SinglePaginatedResponse<any>> => {
    try {
      const response: AxiosResponse<SinglePaginatedResponse<any>> =
        await axios.post(
          `${getCalendarBaseUrl()}/${propertyId}/rules`,
          rulesRequest,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
              'Content-Type': 'application/json',
            },
          },
        );
      return response.data;
    } catch (error) {
      console.error('Error updating rules:', error);
      throw error;
    }
  },

  /**
   * Apply a cancellation policy catalog code over a date range (per-day).
   * Backend skips dates with no price row and returns the count it actually updated.
   * @param propertyId - The property ID
   * @param payload - { fromDate, toDate, cancellationPolicyCode }
   * @returns Promise with response ({ result: { updatedDates } })
   */
  setCancellationPolicy: async (
    propertyId: number | string,
    payload: CancellationPolicyRangeRequest,
  ): Promise<{ updatedDates?: number } & Record<string, any>> => {
    try {
      const response: AxiosResponse<SinglePaginatedResponse<any>> =
        await axios.post(
          `${getCalendarBaseUrl()}/${propertyId}/cancellation`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
              'Content-Type': 'application/json',
            },
          },
        );
      return response?.data?.result ?? {};
    } catch (error) {
      console.error('Error setting cancellation policy:', error);
      throw error;
    }
  },

  /**
   * Sync host calendar (availability and/or pricing) from an Airbnb listing URL.
   * Backend route must match; adjust path if your API uses a different name.
   */
  syncAirbnbListing: async (
    propertyId: string,
    request: AirbnbCalendarSyncRequest,
  ): Promise<SinglePaginatedResponse<any>> => {
    try {
      const response: AxiosResponse<SinglePaginatedResponse<any>> =
        await axios.post(
          `${getCalendarBaseUrl()}/${propertyId}/syncAirbnb`,
          request,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
              'Content-Type': 'application/json',
            },
          },
        );
      return response.data;
    } catch (error) {
      console.error('Error syncing Airbnb listing:', error);
      throw error;
    }
  },

  /**
   * Force-poll every enabled iCal subscription for a property. Bypasses the scheduler's
   * 5-minute recency skip — every request hits the foreign feed.
   */
  reloadIcalForProperty: async (
    propertyId: string | number,
  ): Promise<any> => {
    try {
      const response: AxiosResponse<any> = await axios.post(
        `${API_BASE_URI}/host/ical/import/property/${propertyId}/poll`,
        null,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error('Error reloading iCal for property:', error);
      throw error;
    }
  },

  /**
   * Get the existing outbound iCal export token for a property, or mint one if none exists.
   * Backend is idempotent — repeated calls return the same URL until rotation.
   * Response shape: { propertyId, token, url, createdAt }
   */
  getOrCreateIcalExportToken: async (
    propertyId: string | number,
  ): Promise<{ propertyId: number; token: string; url: string; createdAt: string }> => {
    try {
      const response: AxiosResponse<any> = await axios.post(
        `${API_BASE_URI}/host/ical/export/${propertyId}/token`,
        null,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error('Error generating iCal export token:', error);
      throw error;
    }
  },

  /**
   * Fetch every iCal subscription configured for a property. Used to pre-fill the host UI
   * with whatever URL was previously submitted.
   */
  getIcalSubscriptions: async (
    propertyId: string | number,
  ): Promise<Array<{ id: number; propertyId: number; source: string; feedUrl: string; enabled: boolean }>> => {
    try {
      const response: AxiosResponse<any> = await axios.get(
        `${API_BASE_URI}/host/ical/import/${propertyId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
          },
        },
      );
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching iCal subscriptions:', error);
      return [];
    }
  },

  /**
   * Register (or refresh) the Airbnb iCal subscription for a property.
   * Backend stores the URL and the scheduler polls it every 15 minutes.
   */
  addIcalSubscription: async (
    propertyId: string | number,
    feedUrl: string,
  ): Promise<any> => {
    try {
      const response: AxiosResponse<any> = await axios.post(
        `${API_BASE_URI}/host/ical/import`,
        {
          propertyId: Number(propertyId),
          source: 'ICAL_AIRBNB',
          feedUrl: feedUrl.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
            'Content-Type': 'application/json',
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error('Error adding iCal subscription:', error);
      throw error;
    }
  },
};

export default CalendarService;
